/**
 * Code-based route tree (file-based routing needs a bundler plugin Bun doesn't
 * run). App screens (the log, the overview and its pediatrician report,
 * connecting an AI assistant, settings, the end-of-setup welcome), publicly
 * accessible legal, support, and comparison pages, and the two screens an AI
 * assistant's OAuth flow sends a parent to (/login, /consent).
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
} from "@tanstack/react-router";
import { RootLayout } from "./components/layout.tsx";
import { Assistant } from "./routes/assistant.tsx";
import { Consent } from "./routes/consent.tsx";
import { Log } from "./routes/log.tsx";
import { Overview } from "./routes/overview.tsx";
import { Report } from "./routes/report.tsx";
import { Settings } from "./routes/settings.tsx";
import { Setup } from "./routes/setup.tsx";
import { Welcome } from "./routes/welcome.tsx";
import { Privacy, Support, Terms } from "./routes/legal.tsx";

const rootRoute = createRootRoute({ component: RootLayout });

const logRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Log });
const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overview",
  component: Overview,
});
const reportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/report",
  component: Report,
});
const assistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assistant",
  component: Assistant,
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  component: Setup,
});
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: Welcome,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: Settings,
});
// The OAuth provider's login page. Signed out, the root layout shows the login
// screen (which resumes the authorization); signed in, there's nothing to do.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => <Navigate to="/" replace />,
});
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consent",
  component: Consent,
});
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: Privacy,
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: Terms,
});
const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: Support,
});
// Public, like the legal pages, and split out: its table is no use inside the app.
const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/compare",
  component: lazyRouteComponent(() => import("./routes/compare.tsx"), "Compare"),
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    logRoute,
    overviewRoute,
    reportRoute,
    assistantRoute,
    setupRoute,
    welcomeRoute,
    settingsRoute,
    loginRoute,
    consentRoute,
    privacyRoute,
    termsRoute,
    supportRoute,
    compareRoute,
  ]),
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
